import { Dispatch, SetStateAction, useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DeleteDocumentsDialog } from './delete-documents-dialog';
import { ServerSideFilterPopover } from './server-side-filter-popover';
import {
  DocWithCasesResponseDto,
  MatterDocsWithCasesFilters,
} from '../../../../../../../../backend/src/matter/doc/dto/doc-with-cases.response.dto';
import { useDeleteMatterDocs } from '@/api/mutations/useDeleteMatterDocs';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { ProcessingChip, getProcessingState } from '@/new/components/processing-chip';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ServerSideScrollableTable } from '@/new/components/table/server-side-scrollable-table';
import { TableNullField } from '@/new/components/table/table-null-field';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { formatDatetimeForTable, sortStringWithEmptyLast } from '@/new/lib/table-utils';
import { cn } from '@/new/lib/utils';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';
import { useStore } from '@/store';

const fallbackTableData: DocWithCasesResponseDto[] = [];
const columnHelper = createColumnHelper<DocWithCasesResponseDto>();

const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className="flex w-full items-center justify-center text-center">
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        />
      </div>
    ),
    cell: (info) => (
      <div className="flex w-full min-w-5 items-center justify-center text-center">
        <Checkbox
          checked={info.row.getIsSelected()}
          disabled={!info.row.getCanSelect()}
          onCheckedChange={info.row.getToggleSelectedHandler()}
        />
      </div>
    ),
  }),
  columnHelper.accessor('file.name', {
    id: 'file.name',
    header: () => <span>Filename</span>,
    cell: (info) => <div className="line-clamp-1 max-w-96 min-w-32 break-all">{info.getValue()}</div>,
    sortingFn: sortStringWithEmptyLast,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('cases', {
    header: () => <span>Subsets</span>,
    cell: (info) => {
      const cases = info.getValue();

      if (!cases.length) return <TableNullField />;

      return (
        <Popover>
          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="gap-0 py-0.5">
              <span>
                {cases.length} <span className="font-normal">{cases.length === 1 ? 'subset' : 'subsets'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-min" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-2.5">
              {cases.map((c) => (
                <Chip key={c.id} className={cn(CHIP_COLOURS.default)}>
                  <Icon className="lucide--group" />
                  {c.name}
                </Chip>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    },
  }),
  columnHelper.accessor('isProcessed', {
    header: () => <span>Status</span>,
    cell: (info) => {
      const doc = info.row.original;
      const processingState = getProcessingState(doc);
      return <ProcessingChip processingState={processingState} />;
    },
  }),
  columnHelper.accessor('createdDate', {
    id: 'createdDate',
    header: () => <span>Uploaded</span>,
    cell: (info) => formatDatetimeForTable(info.getValue()),
  }),
  columnHelper.display({
    id: 'actions',
    header: () => <span>View File</span>,
    cell: (info) => {
      const doc = info.row.original;
      return <ViewDocButton doc={doc} />;
    },
  }),
];

function ViewDocButton({ doc }: { doc: DocWithCasesResponseDto }) {
  const { openReferenceDrawer } = useStore();

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openReferenceDrawer({ docId: doc.id });
  };

  return (
    <Button size="sm" onClick={handleViewClick}>
      View
      <Icon className="lucide--arrow-right" />
    </Button>
  );
}

interface DocsTableProps {
  docs: DocWithCasesResponseDto[] | undefined;
  rowSelection: RowSelectionState;
  onRowSelectionChange: Dispatch<SetStateAction<RowSelectionState>>;
  isLoading: boolean;
  // Server-side pagination
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  pageCount: number;
  totalCount: number;
  // Server-side filtering
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  filters: MatterDocsWithCasesFilters;
  onFiltersChange: (filters: MatterDocsWithCasesFilters) => void;
  // Server-side sorting
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  // Permissions
  canDeleteDocuments?: boolean;
}

export function DocsTable({
  docs,
  rowSelection,
  onRowSelectionChange,
  isLoading,
  pagination,
  onPaginationChange,
  pageCount,
  totalCount,
  searchQuery,
  onSearchQueryChange,
  filters,
  onFiltersChange,
  sorting,
  onSortingChange,
  canDeleteDocuments = true,
}: DocsTableProps) {
  const { matterId } = useParams();
  if (!matterId) return null;

  const processingState = useMatterProcessingStore((state) => state.getProcessingState(matterId));
  const isProcessing = processingState?.hasProcessingRuns ?? false;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  function clearAllFilters() {
    onSearchQueryChange('');
    onFiltersChange({});
  }

  const table = useReactTable({
    data: docs ?? fallbackTableData,
    columns,
    state: {
      rowSelection,
      pagination,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
    // Server-side pagination
    manualPagination: true,
    manualSorting: true,
    pageCount,
    onRowSelectionChange: onRowSelectionChange,
    onPaginationChange: onPaginationChange,
    onSortingChange: onSortingChange,
  });

  // Get selected document IDs
  const selectedDocIds = Object.keys(rowSelection);
  const { mutate: deleteDocs, isLoading: isDeleting } = useDeleteMatterDocs(matterId, () => {
    toast.success(`Documents deleting...`);
    setDeleteDialogOpen(false);
    onRowSelectionChange({});
  });

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedDocIds.length > 0) {
      deleteDocs(selectedDocIds);
    }
  };

  return (
    <>
      <ServerSideScrollableTable
        table={table}
        searchQuery={searchQuery}
        onSearchQueryChange={onSearchQueryChange}
        clearAllFilters={clearAllFilters}
        filterPopover={
          <ServerSideFilterPopover
            filters={filters}
            onFiltersChange={onFiltersChange}
            clearAllFilters={clearAllFilters}
          />
        }
        plural="documents"
        totalCount={totalCount}
        additionalButtons={
          isProcessing || !canDeleteDocuments ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex">
                  <Button disabled variant="destructive">
                    <Icon className="lucide--trash-2" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isProcessing
                    ? 'Cannot delete documents while processing is in progress'
                    : 'You do not have permission to delete documents'}
                </p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button disabled={selectedDocIds.length === 0} variant="destructive" onClick={handleDeleteClick}>
              <Icon className="lucide--trash-2" />
            </Button>
          )
        }
        className="px-0"
        loading={isLoading}
      />
      <DeleteDocumentsDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        selectedCount={selectedDocIds.length}
      />
    </>
  );
}
